import { useMemo, useState } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { useCollectionBinding, EditableCollectionBinding } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import {
  classifyPhoto,
  GalleryLightbox,
  GallerySharedStyles,
  type GalleryImage,
} from './_galleryShared';

/**
 * Unified photo gallery — a single masonry grid of every photo in the
 * property `photo_gallery` collection (with its lightbox). Two columns of
 * images at staggered aspect ratios, packed with a tight gutter.
 *
 * Weidner Homestead override: the Homestead parent swaps this grid for a
 * single-image carousel below 640px. Customers asked for a grid on mobile
 * too, so the same two-column masonry is used at every breakpoint. Also
 * adds Community / Apartment filter toggles (same behavior as Vantage
 * PhotoGallery) styled to match Homestead CTAs — squared, tracked
 * type, secondary fill when active.
 *
 * The page hero (HeroSolidBG, `blurbField: property_gallery_blurb`) supplies
 * the "Gallery" title and intro paragraph, so this section is intentionally
 * heading-less — it is just the filters + grid.
 */

type GalleryFilter = 'all' | 'community' | 'apartment';

const FILTER_OPTIONS: { value: GalleryFilter; label: string }[] = [
  { value: 'all', label: 'All Photos' },
  { value: 'community', label: 'Community' },
  { value: 'apartment', label: 'Apartment' },
];

export function GalleryGrid() {
  const images = useCollectionBinding<GalleryImage>('gallerygrid.images', {
    collection: 'photo_gallery',
  });

  const [filter, setFilter] = useState<GalleryFilter>('all');
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  const photoList = useMemo(() => {
    const list = images && images.length > 0 ? images : [];
    const excludedTitles = new Set(['other', 'floorplan', 'logo']);
    return list.filter(
      (img) =>
        Boolean(img.image) &&
        !excludedTitles.has((img.title || '').toLowerCase().trim()),
    );
  }, [images]);

  // Which category toggles actually have photos to show.
  const availableFilters = useMemo(() => {
    const present = new Set(photoList.map((img) => classifyPhoto(img)));
    return FILTER_OPTIONS.filter(
      (opt) => opt.value === 'all' || present.has(opt.value),
    );
  }, [photoList]);

  const showFilters = availableFilters.length > 1;

  const filteredPhotos = useMemo(() => {
    if (filter === 'all') return photoList;
    return photoList.filter((img) => classifyPhoto(img) === filter);
  }, [photoList, filter]);

  const columns: { img: GalleryImage; index: number }[][] = [[], []];
  filteredPhotos.forEach((img, i) => columns[i % 2].push({ img, index: i }));

  const handleFilterChange = (next: GalleryFilter) => {
    setFilter(next);
    setSelectedIndex(null);
  };

  if (photoList.length === 0) {
    return (
      <section className="bg-white">
        <div className="py-12 text-center text-[var(--vibe-color-text-muted)]">
          <p className="font-body text-lg">Gallery images will be displayed here.</p>
        </div>
        <GalleryFilterStyles />
      </section>
    );
  }

  return (
    <section className="bg-white">
      <EditableCollectionBinding
        bindingPath="gallerygrid.images"
        label="Gallery Photos"
      >
        <div className="gallery-grid__container">
          {showFilters && (
            <div
              className="gallery-filters"
              role="group"
              aria-label="Filter gallery photos"
            >
              {availableFilters.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  className={`gallery-filter${filter === opt.value ? ' gallery-filter--active' : ''}`}
                  aria-pressed={filter === opt.value}
                  onClick={() => handleFilterChange(opt.value)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          )}

          {filteredPhotos.length === 0 ? (
            <div className="py-12 text-center text-[var(--vibe-color-text-muted)]">
              <p className="font-body text-lg">No photos in this category.</p>
            </div>
          ) : (
            <div className="gallery-grid__masonry">
              {columns.map((col, ci) => (
                <div className="gallery-grid__col" key={ci}>
                  {col.map(({ img, index }, pos) => {
                    const isTall = (ci + pos) % 2 === 1;
                    return (
                      <button
                        key={index}
                        type="button"
                        className="gallery-grid__item gallery-item"
                        style={{ aspectRatio: isTall ? '5 / 6' : '5 / 3' }}
                        onClick={() => setSelectedIndex(index)}
                        aria-label={`Open image ${index + 1}`}
                      >
                        <VibesitesImage
                          url={img.image}
                          alt={img.alt_text || img.title || ''}
                          className="gallery-grid__img"
                          sizes="(max-width: 639px) 50vw, (max-width: 1080px) 45vw, 530px"
                        />
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>
          )}
        </div>
      </EditableCollectionBinding>

      <GalleryLightbox
        images={filteredPhotos}
        selectedIndex={selectedIndex}
        onClose={() => setSelectedIndex(null)}
        onChange={setSelectedIndex}
      />

      <GallerySharedStyles />
      <GalleryFilterStyles />

      <style>{`
        .gallery-grid__container {
          max-width: 1080px;
          margin-left: auto;
          margin-right: auto;
          padding: 2.5rem 1.5rem 3.5rem;
        }

        .gallery-grid__masonry {
          display: flex;
          align-items: flex-start;
          gap: 0.625rem;
        }

        .gallery-grid__col {
          flex: 1 1 0;
          min-width: 0;
          display: flex;
          flex-direction: column;
          gap: 0.625rem;
        }

        .gallery-grid__item {
          display: block;
          width: 100%;
          padding: 0;
          border: 0;
          background: none;
          overflow: hidden;
          cursor: pointer;
          position: relative;
          line-height: 0;
        }

        .gallery-grid__img {
          display: block;
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.6s ease;
        }

        .gallery-grid__item:hover .gallery-grid__img,
        .gallery-grid__item:focus-visible .gallery-grid__img {
          transform: scale(1.05);
        }

        .gallery-grid__item:focus-visible {
          outline: 2px solid var(--vibe-color-primary);
          outline-offset: 2px;
        }

        @media (prefers-reduced-motion: reduce) {
          .gallery-grid__img { transition: none; }
        }

        @media (max-width: 639px) {
          .gallery-grid__container {
            padding: 1.5rem 0.75rem 2.5rem;
          }
          .gallery-grid__masonry,
          .gallery-grid__col {
            gap: 0.5rem;
          }
        }
      `}</style>
    </section>
  );
}

/** Homestead-styled Community / Apartment filter row (squared CTAs, not pills). */
function GalleryFilterStyles() {
  return (
    <style>{`
      .gallery-filters {
        display: flex;
        flex-wrap: wrap;
        justify-content: center;
        gap: 0.5rem;
        margin-bottom: 1.5rem;
      }

      @media (min-width: 768px) {
        .gallery-filters {
          gap: 0.625rem;
          margin-bottom: 2rem;
        }
      }

      .gallery-filter {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        padding: 0.65rem 1.35rem;
        border: 1px solid var(--vibe-color-border, rgba(0, 0, 0, 0.15));
        border-radius: 2px;
        background: transparent;
        font-family: var(--vibe-font-heading);
        font-size: 0.8125rem;
        font-weight: 600;
        letter-spacing: 0.08em;
        text-transform: uppercase;
        color: var(--vibe-color-text, #1a1a1a);
        cursor: pointer;
        white-space: nowrap;
        transition: background-color 0.25s ease, border-color 0.25s ease, color 0.25s ease;
      }

      .gallery-filter:hover {
        border-color: var(--vibe-color-secondary);
        color: var(--vibe-color-secondary);
      }

      .gallery-filter--active {
        background: var(--vibe-color-secondary);
        border-color: var(--vibe-color-secondary);
        color: var(--vibe-color-text-on-secondary, #ffffff);
      }

      .gallery-filter--active:hover {
        background: color-mix(in srgb, var(--vibe-color-secondary), black 12%);
        border-color: color-mix(in srgb, var(--vibe-color-secondary), black 12%);
        color: var(--vibe-color-text-on-secondary, #ffffff);
      }

      .gallery-filter:focus-visible {
        outline: 2px solid var(--vibe-color-secondary);
        outline-offset: 2px;
      }
    `}</style>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'GalleryGrid',
  displayName: 'Photo Gallery',
  tokens: {},
  collectionBindings: {
    'gallerygrid.images': {
      label: 'Gallery Photos',
      collection: 'photo_gallery',
      description:
        'Every photo from the property photo gallery, shown together in one grid.',
    },
  },
};

(GalleryGrid as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default GalleryGrid;
